class ScrollBar extends HTMLElement {
  constructor() {
    super();
    this.hideScrollbarTimeout = null;
    this.isDragging = false;
    this.track = null;
    this.thumb = null;
    this.trackHeight = 0;
    this.thumbHeight = 0;

    // Bound methods
    this._onResize = this._onResize.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._onMouseEnter = this._onMouseEnter.bind(this);
    this._onMouseLeave = this._onMouseLeave.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
  }

  get isDesktop() {
    return window.innerWidth > 1024;
  }

  connectedCallback() {
    if (!this.isDesktop) return;
    this.track = this.querySelector('.inner');
    this.thumb = this.querySelector('.thumb');
    if (!this.track || !this.thumb) return;

    this._calculateDimensions();
    this._updateThumbPosition();

    window.addEventListener('resize', this._onResize);
    window.addEventListener('scroll', this._onScroll);
    this.addEventListener('mouseenter', this._onMouseEnter);
    this.addEventListener('mouseleave', this._onMouseLeave);
    this.thumb.addEventListener('pointerdown', this._onPointerDown);
  }

  disconnectedCallback() {
    clearTimeout(this.hideScrollbarTimeout);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('scroll', this._onScroll);
    this.removeEventListener('mouseenter', this._onMouseEnter);
    this.removeEventListener('mouseleave', this._onMouseLeave);
    if (this.thumb) {
      this.thumb.removeEventListener('pointerdown', this._onPointerDown);
      this.thumb.removeEventListener('pointermove', this._onPointerMove);
      this.thumb.removeEventListener('pointerup', this._onPointerUp);
      this.thumb.removeEventListener('pointercancel', this._onPointerUp);
    }
  }

  _calculateDimensions() {
    this.trackHeight = this.track.offsetHeight;
    this.thumbHeight = Math.max(
      (window.innerHeight / document.documentElement.scrollHeight) * this.trackHeight,
      30
    );
    this.thumb.style.height = `${this.thumbHeight}px`;
  }

  _updateThumbPosition() {
    const scrollLimit = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollLimit <= 0) return;
    const scrollableDistance = this.trackHeight - this.thumbHeight;
    const thumbTop = (window.scrollY / scrollLimit) * scrollableDistance;
    this.thumb.style.transform = `translate3d(0, ${thumbTop}px, 0)`;
  }

  _showScrollbar() {
    document.documentElement.classList.add('show-scrollbar');
    clearTimeout(this.hideScrollbarTimeout);
  }

  _hideScrollbarAfterDelay() {
    clearTimeout(this.hideScrollbarTimeout);
    this.hideScrollbarTimeout = setTimeout(() => {
      document.documentElement.classList.remove('show-scrollbar');
    }, 1000);
  }

  _onResize() {
    if (!this.isDesktop) return;
    this._calculateDimensions();
    this._updateThumbPosition();
  }

  _onScroll() {
    this._updateThumbPosition();
    this._showScrollbar();
    if (!this.isDragging) this._hideScrollbarAfterDelay();
  }

  _onMouseEnter() {
    this._showScrollbar();
  }

  _onMouseLeave() {
    if (!this.isDragging) this._hideScrollbarAfterDelay();
  }

  _onPointerDown(e) {
    e.preventDefault();
    this.startY = e.clientY;
    this.startScrollPos = window.scrollY;
    this.isDragging = true;
    this.thumb.classList.add('grabbing');
    document.body.classList.add('dragging-scrollbar');
    this._showScrollbar();
    this.thumb.setPointerCapture(e.pointerId);
    this.thumb.addEventListener('pointermove', this._onPointerMove);
    this.thumb.addEventListener('pointerup', this._onPointerUp);
    this.thumb.addEventListener('pointercancel', this._onPointerUp);
  }

  _onPointerMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();
    const deltaY = e.clientY - this.startY;
    const scrollableTrackDistance = this.trackHeight - this.thumbHeight;
    const scrollableDocDistance = document.documentElement.scrollHeight - window.innerHeight;
    const scrollRatio = scrollableDocDistance / scrollableTrackDistance;
    const newScrollPos = this.startScrollPos + deltaY * scrollRatio;
    window.scrollTo({
      top: Math.max(0, Math.min(newScrollPos, scrollableDocDistance)),
      behavior: 'auto',
    });
  }

  _onPointerUp(e) {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.thumb.classList.remove('grabbing');
    document.body.classList.remove('dragging-scrollbar');
    this.thumb.releasePointerCapture(e.pointerId);
    this.thumb.removeEventListener('pointermove', this._onPointerMove);
    this.thumb.removeEventListener('pointerup', this._onPointerUp);
    this.thumb.removeEventListener('pointercancel', this._onPointerUp);
    this._hideScrollbarAfterDelay();
  }
}

customElements.define('scroll-bar', ScrollBar);
